import React, { useCallback, useRef, useState } from "react";
import clsx from "clsx";
import { Box, Typography } from "@material-ui/core";
import { useTextAreaStyles } from "./TextArea.style";

export type TextAreaResize = "none" | "vertical" | "both";

export interface TextAreaProps {
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  id?: string;
  rows?: number;
  minHeight?: number;
  label?: React.ReactNode;
  labelHint?: React.ReactNode;
  maxCount?: number;
  hideCount?: boolean;
  /** Optional inline style for the main label (color/weight overrides) */
  labelStyle?: React.CSSProperties;
  error?: boolean;
  helperText?: string;
  className?: string;
  textareaClassName?: string;
  validate?: {
    maxLength?: number;
    required?: boolean;
    requiredMessage?: string;
  };
  setError?: (hasError: boolean) => void;
  resize?: TextAreaResize;
  /** Show validation styling without waiting for first blur (e.g. wizard step revisit) */
  validateInitiallyTouched?: boolean;
}

function runValidation(
  value: string,
  validate: TextAreaProps["validate"]
): string {
  if (!validate) return "";
  if (validate.required && !value.trim()) {
    return validate.requiredMessage ?? "This field is required";
  }
  if (validate.maxLength != null && value.length > validate.maxLength) {
    return `The input exceeds the ${validate.maxLength} character limit.`;
  }
  return "";
}

/**
 * Reusable textarea: label row, optional hint, character count, enabled (#171C1E border) / disabled (no border, gray fill).
 */
export const TextArea: React.FC<TextAreaProps> = ({
  name,
  value,
  onChange,
  onBlur,
  placeholder,
  disabled = false,
  readOnly = false,
  id: idProp,
  rows = 4,
  minHeight,
  label,
  labelHint,
  maxCount,
  hideCount = false,
  labelStyle,
  error: errorProp,
  helperText: helperTextProp,
  className,
  textareaClassName,
  validate,
  setError,
  resize = "vertical",
  validateInitiallyTouched = false,
}) => {
  const classes = useTextAreaStyles();
  const stableDomId = useRef(
    `modus-textarea-${Math.random().toString(36).slice(2, 11)}`
  );
  const fieldId = idProp ?? stableDomId.current;
  const [internalError, setInternalError] = useState("");
  const [fieldBlurred, setFieldBlurred] = useState(
    () => Boolean(validateInitiallyTouched)
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLTextAreaElement>) => {
      setFieldBlurred(true);
      if (validate && !disabled && !readOnly) {
        const msg = runValidation(e.target.value, validate);
        setInternalError(msg);
        setError?.(msg !== "");
      }
      onBlur?.(e);
    },
    [onBlur, validate, disabled, readOnly, setError]
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const v = e.target.value;
      if (validate && fieldBlurred && !disabled && !readOnly) {
        const msg = runValidation(v, validate);
        setInternalError(msg);
        setError?.(msg !== "");
      }
      onChange(e);
    },
    [onChange, validate, fieldBlurred, disabled, readOnly, setError]
  );

  const showCount = maxCount != null && !hideCount;
  const hasValidationIssue =
    Boolean(errorProp) || Boolean(internalError);
  const hasError = fieldBlurred && hasValidationIssue;
  const displayHelper = hasError ? (helperTextProp ?? internalError) : "";
  const effectiveResize = disabled || readOnly ? "none" : resize;

  return (
    <Box className={clsx(classes.root, className)}>
      {(label != null || labelHint != null || showCount) && (
        <Box className={classes.headerRow}>
          <Box className={classes.labelGroup}>
            {label != null && (
              <Typography
                component="span"
                className={clsx(
                  classes.label,
                  disabled && !labelStyle && classes.labelDisabled
                )}
                style={labelStyle}
              >
                {label}
              </Typography>
            )}
            {labelHint != null && (
              <Typography
                component="span"
                className={clsx(
                  classes.labelHint,
                  disabled && classes.labelHintDisabled
                )}
              >
                {label != null ? <> {labelHint}</> : labelHint}
              </Typography>
            )}
          </Box>
          {showCount && (
            <Typography
              component="span"
              className={clsx(
                classes.charCount,
                disabled && classes.charCountDisabled
              )}
            >
              {value.length} / {maxCount}
            </Typography>
          )}
        </Box>
      )}

      <Box
        className={clsx(
          classes.fieldShell,
          hasError && !disabled && !readOnly && classes.fieldShellError,
          (disabled || readOnly) && classes.fieldShellDisabled
        )}
      >
        <textarea
          id={fieldId}
          name={name}
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={readOnly}
          rows={rows}
          aria-invalid={hasError}
          aria-describedby={displayHelper ? `${fieldId}-helper` : undefined}
          className={clsx(
            classes.textarea,
            disabled && classes.textareaDisabled,
            textareaClassName
          )}
          style={{
            minHeight: minHeight ?? undefined,
            resize: effectiveResize,
          }}
        />
      </Box>

      {displayHelper ? (
        <Typography
          id={`${fieldId}-helper`}
          component="p"
          className={clsx(hasError && classes.helper)}
          role={hasError ? "alert" : undefined}
        >
          {displayHelper}
        </Typography>
      ) : null}
    </Box>
  );
};

export default TextArea;
